/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { ButtonPrimary } from '@/components/button'
import { EventItem } from '@/components/common'
import { ConfirmModal } from '@/components/modal'
import { EventItemLoading } from '@/components/skeleton'
import { CLUB_MODIFY, EVENT_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { useGetClubEvents, useRemoveEvent } from '@/services'
import { Event } from '@/types'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { clubSelector } from '../selectors'

export const ClubEvents = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const { permissions } = useAppSelector(clubSelector)

  const { clubId } = params

  const { mutate: getClubEvents, data: resClubEvents, isLoading } = useGetClubEvents()
  const { mutate: deleteEvent } = useRemoveEvent()

  const [offset, setOffset] = useState(OFFSET)
  const [clubEvents, setClubEvents] = useState<Event[]>([])
  const [openConfirmModal, setOpenConfirmModal] = useState<number | null>(null)
  const [lastElement, setLastElement] = useState<any>(null)

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onOpenConfirmModal = (id: number) => setOpenConfirmModal(id)

  const onCloseConfirmModal = () => setOpenConfirmModal(null)

  const onNavigateCreateEvent = () => {
    navigate(`${ROUTE_CLUBS}/${clubId}/create-event`)
  }

  const onDeleteEvent = () => {
    if (openConfirmModal) {
      onCloseConfirmModal()
      deleteEvent(openConfirmModal, {
        onSuccess: () => {
          toast.success('You have successfully deleted this event')
          setClubEvents([])
          if (clubId)
            getClubEvents({
              clubId: Number(clubId),
              limit: LIMIT,
              offset: offset === OFFSET ? offset : OFFSET,
              includeDraftEvents: has(permissions, EVENT_MODIFY),
            })
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      })
    }
  }

  useEffect(() => {
    if (clubId)
      getClubEvents({
        clubId: Number(clubId),
        limit: LIMIT,
        offset: offset,
        includeDraftEvents: has(permissions, EVENT_MODIFY),
      })
  }, [clubId, offset])

  useEffect(() => {
    if (resClubEvents) setClubEvents([...clubEvents, ...resClubEvents.result.hits])
  }, [resClubEvents])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <>
      <div>
        <div className="flex justify-between">
          <h4 className="text-xl text-white font-semibold">{t('common.events')}</h4>
          {has(permissions, CLUB_MODIFY) ? (
            <ButtonPrimary
              onClick={onNavigateCreateEvent}
              title="New Event"
              // disabled={isInternationalAccount ? false : !isAccountSetup}
              className="new-event"
            />
          ) : null}
        </div>

        <div className="mt-3 flex flex-col gap-y-4">
          {clubEvents.map((item, index) => {
            return index === clubEvents.length - 1 && resClubEvents?.result.hasMore ? (
              <div key={item.leagueId} ref={setLastElement}>
                <EventItem
                  event={item}
                  onOpenConfirmModal={() => onOpenConfirmModal(item.leagueId)}
                  showDeleteEvent={has(permissions, EVENT_MODIFY) && item.registeredMembers < 1}
                />
              </div>
            ) : (
              <EventItem
                key={item.leagueId}
                event={item}
                onOpenConfirmModal={() => onOpenConfirmModal(item.leagueId)}
                showDeleteEvent={has(permissions, EVENT_MODIFY) && item.registeredMembers < 1}
              />
            )
          })}

          {isLoading && (
            <>
              <EventItemLoading />
              <EventItemLoading />
              <EventItemLoading />
            </>
          )}

          {resClubEvents && !resClubEvents?.result?.hasMore && (
            <div className="flex justify-center">
              <p className="text-slate-300 text-base font-semibold">{t('common.noMoreResults')}</p>
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        open={!!openConfirmModal}
        onClose={onCloseConfirmModal}
        onSubmit={onDeleteEvent}
        title={t('common.deleteEvent')}
        desc={t('common.deleteEventDesc')}
        submitTitle={t('common.confirm')}
      />
    </>
  )
}
